import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  AuthContext,
  clearStoredUser,
  getStoredUser,
  storeUser,
  type AuthContextShape,
} from "./lib/auth";

const Home = lazy(() => import("./pages/Home"));
const Analyze = lazy(() => import("./pages/Analyze"));
const Login = lazy(() => import("./pages/Login"));

const Loading: React.FC = () => (
  <div className="min-h-screen grid place-items-center text-slate-400">
    Loading…
  </div>
);

const App: React.FC = () => {
  const [user, setUser] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Rehydrate the session on first mount so a page refresh keeps the user
  // logged in for the duration of the browser session.
  useEffect(() => {
    setUser(getStoredUser());
    setReady(true);
  }, []);

  const signIn = useCallback((username: string) => {
    storeUser(username);
    setUser(username);
  }, []);

  const signOut = useCallback(() => {
    clearStoredUser();
    setUser(null);
  }, []);

  const authValue = useMemo<AuthContextShape>(
    () => ({ user, signIn, signOut }),
    [user, signIn, signOut],
  );

  // Avoid a flash of "Login" on refresh while we read sessionStorage.
  if (!ready) return null;

  return (
    <HelmetProvider>
      <AuthContext.Provider value={authValue}>
        <Router>
          <Suspense fallback={<Loading />}>
            {user ? (
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/analyze" element={<Analyze />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            ) : (
              <Login />
            )}
          </Suspense>
        </Router>
      </AuthContext.Provider>
    </HelmetProvider>
  );
};

export default App;
